import { useRef } from 'react';
import _ from 'lodash';
import { AI_COMMAND, EMITTER_CONSTANTS } from '@/shared/config';
import { useEmitter } from '@/shared/lib/customHooks';
import { generateDefaultHeatmapState } from '@/shared/lib/heatmap';
import { removeSavingData } from '@/entities/beat';
import { logError } from '@/shared/lib/apiHelpers';
import { clearApolloCache } from '@/shared/api';
import emitter from '@/shared/lib/eventEmitter';
import { DATA_HEATMAP_STATUS } from '@/shared/model';
import { selectIsActiveTab, useReportStore } from '@/entities/report';

type HrSummaryDataEffectProps = { keyRecoil: string };

/**
 * Renderless effect component that wires the HR summary tab to the AI socket stream, applying event, minutely-summary and save-completion messages to the report store. Subscribes to EVENTSUPDATED_EVENT, RE_CAPTURE_AUTO_EVENTS, MINUTELY_HR_UPDATED and STOP_SAVING, ignoring them unless this tab is active; while the push-queue flag is set the event and summary messages are buffered in a ref and replayed once saving reports done, at which point the Apollo cache is cleared, in-flight beat and event edits are dropped and the ECG viewer is re-rendered.
 *
 * @param keyRecoil - The store key identifying this tab's slice of report state.
 * @returns Null, since the component only registers socket effects.
 */
const HrSummaryDataEffect = ({ keyRecoil }: HrSummaryDataEffectProps): null => {
  // use recoil setter
  const setBeatChangesAction = useReportStore((s) => s.actions.setBeatChanges);
  /**
   * Replaces the pending beat changes held for this tab. Forwards the value to the shared beat-changes action, binding it to this tab's store key.
   *
   * @param v - The beat changes, or an updater receiving the previous list.
   * @returns Nothing.
   */
  const setBeatChanges = (v: Parameters<typeof setBeatChangesAction>[1]) =>
    setBeatChangesAction(keyRecoil, v);
  const setBeatOptionsAction = useReportStore((s) => s.actions.setBeatOptions);
  /**
   * Replaces the beat rendering and edit options held for this tab. Forwards the value to the shared beat-options action, binding it to this tab's store key.
   *
   * @param v - The beat options object, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setBeatOptions = (v: Parameters<typeof setBeatOptionsAction>[1]) =>
    setBeatOptionsAction(keyRecoil, v);
  const setReloadHrChartAction = useReportStore((s) => s.actions.setReloadHrChart);
  /**
   * Bumps the counter that forces the heart-rate chart to remount. Forwards the value to the shared reload-HR-chart action, binding it to this tab's store key.
   *
   * @param v - The new counter value, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setReloadHrChart = (v: Parameters<typeof setReloadHrChartAction>[1]) =>
    setReloadHrChartAction(keyRecoil, v);
  const setEventOthersAction = useReportStore((s) => s.actions.setEventOthers);
  /**
   * Replaces the list of other, non-primary ECG events held for this tab. Forwards the value to the shared event-others action, binding it to this tab's store key.
   *
   * @param v - The other events, or an updater receiving the previous list.
   * @returns Nothing.
   */
  const setEventOthers = (v: Parameters<typeof setEventOthersAction>[1]) =>
    setEventOthersAction(keyRecoil, v);
  const setEventOptionsAction = useReportStore((s) => s.actions.setEventOptions);
  /**
   * Replaces the event rendering and edit options held for this tab. Forwards the value to the shared event-options action, binding it to this tab's store key.
   *
   * @param v - The event options object, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setEventOptions = (v: Parameters<typeof setEventOptionsAction>[1]) =>
    setEventOptionsAction(keyRecoil, v);
  const setReloadEcgViewerAction = useReportStore((s) => s.actions.setReloadEcgViewer);
  /**
   * Bumps the counter that forces the ECG viewer to remount. Forwards the value to the shared reload-ECG-viewer action, binding it to this tab's store key.
   *
   * @param v - The new counter value, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setReloadEcgViewer = (v: Parameters<typeof setReloadEcgViewerAction>[1]) =>
    setReloadEcgViewerAction(keyRecoil, v);

  // use recoil value
  const isActiveTab = useReportStore(selectIsActiveTab(keyRecoil));

  // use recoil getter not keyRecoil
  /**
   * Reads the minute-level ECG data map backing the heart-rate chart. Pulls ecgDataMap off a fresh store snapshot rather than subscribing, so socket handlers merge into the live value.
   *
   * @returns The current ECG data map.
   */
  const getEcgDataMap = () => useReportStore.getState().ecgDataMap;

  // use recoil setter not keyRecoil
  const setEcgDataMap = useReportStore((s) => s.actions.setEcgDataMap);
  const setDataHeatmapChartStatus = useReportStore((s) => s.actions.setDataHeatmapChartStatus);
  const setListSelectedCells = useReportStore((s) => s.actions.setListSelectedCells);
  // use recoil state
  const isPushQueue = useReportStore((s) => s.isPushQueue);
  const setIsPushQueue = useReportStore((s) => s.actions.setIsPushQueue);

  const updateDataMessageQueue = useRef([]);
  /**
   * Reloads the heart-rate chart and ECG viewer after the AI pipeline reports updated events. Clears the Apollo cache and bumps both reload counters by a random offset when an outside recapture arrives on the active tab, bumps them alone for an AFib-artifact message, and logs any failure to the console.
   *
   * @param msg - The socket payload, carrying the recapture, recaptureEventsAfterUpdating and isAFibArtifact flags.
   * @param isOutSideCommand - True when the message came from outside the current save cycle rather than the replay queue.
   * @returns A promise resolving once the reload counters have been bumped.
   */
  const handleReloadEvent = async (msg: any, isOutSideCommand = false) => {
    try {
      const { isAFibArtifact } = msg;
      if (isOutSideCommand && msg.isRecapture && msg.recaptureEventsAfterUpdating && isActiveTab) {
        await clearApolloCache();
        setReloadHrChart((prev) => prev + _.round(Math.random() * 100));
        setReloadEcgViewer((prev) => prev + _.round(Math.random() * 100));
        if (!msg.isRecapture) {
          emitter.emit(EMITTER_CONSTANTS.RE_RENDER_ECG_VIEWER);
        }
      } else if (isAFibArtifact) {
        setReloadHrChart((prev) => prev + _.round(Math.random() * 100));
        setReloadEcgViewer((prev) => prev + _.round(Math.random() * 100));
      }
    } catch (error) {
      console.error('Error handling reload event:', error);
    }
  };

  /**
   * Merges freshly computed minutely heart-rate statistics from the socket into the ECG data map. Copies the current map, assigns avgHrs, maxHrs and minHrs onto each matching row by id when summaries are present and the map holds data, writes it back only if it differs from the previous value, then fires RE_RENDER_HR_CHART.
   *
   * @param msg - The socket payload carrying summaries directly or under beatsUpdated.
   * @returns A promise resolving once the map is written and the chart re-render is emitted.
   */
  const handleMinutlySummary = async (msg: any) => {
    const prevEcgDataMap = getEcgDataMap();
    const cloneEcgDataMap = { ...prevEcgDataMap };
    if (
      (msg.summaries?.length || msg.beatsUpdated?.summaries?.length) &&
      !_.isEmpty(prevEcgDataMap?.data)
    ) {
      _.forEach(msg.summaries || msg.beatsUpdated?.summaries, (summary) => {
        const foundData = _.find(cloneEcgDataMap.data, (x) => x.id === summary.id);
        if (foundData) {
          _.assign(foundData, {
            avgHrs: summary?.avgHrs,
            maxHrs: summary?.maxHrs,
            minHrs: summary?.minHrs,
          });
        }
      });
    }
    setEcgDataMap((prev: any) => {
      if (_.isEqual(prev, cloneEcgDataMap)) {
        return prev;
      }
      return cloneEcgDataMap;
    });
    emitter.emit(EMITTER_CONSTANTS.RE_RENDER_HR_CHART);
  };

  /**
   * Discards the beat and event edits that were marked as saving now that the server has accepted them. Strips saving beats out of the beat changes and mirrors the result into the beat options, filters out saving event-others and mirrors that into the event options, then resets the heatmap chart status to its initial state and clears the selected cells.
   *
   * @returns Nothing.
   */
  const removeChangeData = () => {
    let beatsChanges: any[] = [];

    setBeatChanges((prev: any) => {
      if (_.isEmpty(prev)) return prev;
      const beats = removeSavingData(prev);
      beatsChanges = beats;
      return beats;
    });
    setBeatOptions((prev: any) => ({
      ...prev,
      beatChangesState: beatsChanges,
    }));

    let eventOthers: any[] = [];
    setEventOthers((prev: any) => {
      let count = 0;
      const filterResult = prev.filter((x: any) => {
        if (x.isSaving) {
          count += 1;
          return false;
        }
        return true;
      });
      if (count === 0) {
        return prev;
      }
      eventOthers.push(...filterResult);
      return filterResult;
    });
    setEventOptions((prevOptions: any) => ({
      ...prevOptions,
      eventOthersState: eventOthers,
    }));
    setDataHeatmapChartStatus(generateDefaultHeatmapState(DATA_HEATMAP_STATUS.INITIAL));
    setListSelectedCells([]);
    eventOthers = [];
  };

  // TODO SAVE SOCKET
  /**
   * Drains the queued socket messages once the AI save cycle reports completion. Clears the Apollo cache, replays each queued event or minutely-summary message in parallel, drops the locally saved beat and event edits, and in all cases empties the queue, clears the AI loading overlay and re-renders the ECG viewer; errors are logged.
   *
   * @returns A promise resolving once the queue is drained and the viewer refresh is emitted.
   */
  const handleSavingStatus = async () => {
    try {
      await clearApolloCache();
      const promises: Promise<any>[] = [];
      updateDataMessageQueue.current.forEach((x: any) => {
        if (x.type === AI_COMMAND.EVENT) {
          promises.push(handleReloadEvent(x.msg));
        }
        if (x.type === AI_COMMAND.MINUTLY_SUMMARY) {
          promises.push(handleMinutlySummary(x.msg));
        }
      });
      removeChangeData();
      await Promise.all(promises);
    } catch (error) {
      logError('Error: ', error);
    } finally {
      updateDataMessageQueue.current.length = 0;
      emitter.emit(EMITTER_CONSTANTS.AI_LOADING, { isLoading: false });
      emitter.emit(EMITTER_CONSTANTS.RE_RENDER_ECG_VIEWER);
    }
  };

  // EMITTER FROM SOCKET
  useEmitter(
    EMITTER_CONSTANTS.EVENTSUPDATED_EVENT,
    async (msg) => {
      if (!isActiveTab) return;
      if (isPushQueue) {
        updateDataMessageQueue.current.push({
          type: AI_COMMAND.EVENT,
          msg: _.cloneDeep(msg),
        });
      } else {
        await handleReloadEvent(msg, true);
      }
    },
    [isActiveTab, isPushQueue],
  );

  useEmitter(
    EMITTER_CONSTANTS.RE_CAPTURE_AUTO_EVENTS,
    (msg) => {
      if (!isActiveTab) return;
      handleReloadEvent(msg, true);
    },
    [isActiveTab],
  );

  useEmitter(
    EMITTER_CONSTANTS.MINUTELY_HR_UPDATED,
    async (msg) => {
      if (!isActiveTab) return;
      if (isPushQueue) {
        updateDataMessageQueue.current.push({
          type: AI_COMMAND.MINUTLY_SUMMARY,
          msg,
        });
      } else {
        await handleMinutlySummary(msg);
      }
    },
    [isActiveTab, isPushQueue],
  );

  useEmitter(
    EMITTER_CONSTANTS.STOP_SAVING,
    async (msg: any) => {
      if (msg && isActiveTab) {
        if (msg?.data === 'done') {
          setIsPushQueue(false);
          console.time('Execution-time-saving');
          await handleSavingStatus();
          console.timeEnd('Execution-time-saving');
        }
      }
    },
    [isActiveTab],
  );

  return null;
};

export default HrSummaryDataEffect;
